import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { MyThunkDispatch, RootState } from 'store';

import { flightsFilterClear, flightsSearchRequest } from 'action-creators/flights';
import { accountFailure, accountRequest } from 'action-creators/user';
import { flightResults, searchFlights, getFilters } from 'actions/flights';
import { updateAccount } from 'actions/user';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { FlightFilterResponse } from 'models/flightFilter';
import { FlightResultRequestOrderCriteriaEnum } from 'models/flightSearch';
import FlightsList from 'screens/components/FlightsList';
import { amplitudelogEvent, ViewTravelResultsScreenEvent } from 'services/amplitudeService';
import { fetchAccount } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, extractErrorMessage } from 'utils/generic';

const FlightsListContainer = () => {
  const history = useHistory();
  const dispatch = useDispatch<MyThunkDispatch>();
  const {
    departurePoint,
    arrivalPoint,
    departureDate,
    returnDate,
    search,
    cabin,
    baggageIncluded,
    flightTabFilter,
    orderCriteria,
    isLoadingResults,
    traveler,
    departureTime,
    returnTime,
    directFlight,
    branchCompany,
  } = useSelector((state: RootState) => state.flights);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [availableFilters, setAvailableFilters] = useState<FlightFilterResponse>();
  const { t } = useStoreTranslation();
  const toast = useToast();
  const user = useSelector((state: RootState) => state.user.accountResult);
  const isMobile = useIsMobile();

  const handleFlightSearch = useCallback(async () => {
    if (departurePoint && arrivalPoint && departureDate && cabin) {
      dispatch(accountRequest());
      const {
        data: { data },
      } = await fetchAccount();

      if (data.sessionToken === '' || data.sessionToken === undefined || data.sessionToken === null) {
        dispatch(accountFailure('sessionToken is empty'));
        history.replace('/login');
      }

      updateAccount(dispatch, data);
      const payload = {
        baggageIncluded,
        departurePoint,
        arrivalPoint,
        departureDate,
        cabin,
        returnDate: !(departurePoint && !returnDate) ? returnDate : undefined,
        newSessionToken: data.sessionToken,
        traveler,
        departureTime,
        returnTime,
        directFlight,
        branchCompany,
      };
      dispatch(flightsFilterClear());
      dispatch(flightsSearchRequest(payload));
      return await dispatch(searchFlights(payload));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    arrivalPoint,
    baggageIncluded,
    cabin,
    departureDate,
    departurePoint,
    departureTime,
    dispatch,
    returnDate,
    returnTime,
    traveler,
    directFlight,
    branchCompany,
  ]);

  const handleFlightFilter = useCallback(async () => {
    try {
      const data: FlightFilterResponse = await dispatch<any>(getFilters());
      setAvailableFilters(data);
    } catch (e) {
      toast.add({ type: 'danger', message: t('filters.fetchError') });
    }
  }, [dispatch, t, toast]);
  const handleFlightResult = useCallback(async () => dispatch(flightResults(orderCriteria)), [dispatch, orderCriteria]);

  useEffect(() => {
    const handleSearch = async () => {
      const now = new Date();
      const before = search?.resultsAvailableUntil ? new Date(search?.resultsAvailableUntil) : null;
      const hasExpired = before ? before < now : true;

      if (
        (hasExpired || search === null || search === undefined) &&
        (search?.results === null || search?.results === undefined || search?.results.length === 0) &&
        !isLoadingResults &&
        !loadingSearch
      ) {
        try {
          setLoadingSearch(true);
          const res: any = await handleFlightSearch();

          if (res.resultsCount === 0 && isMobile) {
            history.replace('/booking/flights', { showNoResults: true });
            return;
          }

          if (res.resultsCount > 0) {
            //Amplitude
            const event: ViewTravelResultsScreenEvent = {
              SOURCE: 'TRAVEL_HOME',
              RESULTS: res?.resultsCount ?? 0,
              TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
              TRAVEL_FROM: departurePoint?.code ?? '',
              TRAVEL_TO: arrivalPoint?.code ?? '',
              TRAVEL_DESTINATION_TYPE: '',
              TRAVEL_TRIP_TYPE: '',
              TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), departureDate).toString(),
              TRAVEL_DATE_FROM: departureDate?.toString() ?? '',
              TRAVEL_DATE_TO: returnDate?.toString() ?? '',
              TRAVEL_TRIP_LENGTH: (daysCount(departureDate, returnDate) + 1).toString(),
              TRAVEL_PASSENGERS: '1',
              TRAVEL_ADULTS: '1',
              TRAVEL_CHILDREN: '0',
              TRAVEL_INFANTS: '0',
              TRAVEL_CLASS_OF_SERVICE: '',
              TRAVEL_ROOMS: '1',
              TRAVEL_WITH_BAGGAGE: '',
              TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
              SEARCHID: search?.searchId ?? '',
            };

            amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_RESULTS_SCREEN, event, user);

            await Promise.all([handleFlightResult(), handleFlightFilter()]);
          }
        } catch (error) {
          const message = extractErrorMessage(error);

          if (message?.startsWith('Currency reported is different')) {
            Sentry.captureMessage('[Flights] Currency reported is different');
            toast.add({ type: 'danger', message: t('flightsList.error.differentCurrency') });
            history.replace('/booking/flights');
            return;
          }

          if (message?.toLowerCase().includes('cannot search for flights departing before')) {
            history.replace('/booking/flights', { showNoResults: true });
            return;
          }

          toast.add({ type: 'danger', message: message || t('defaultError.default') });
          history.replace('/booking/flights', { showNoResults: true });
        } finally {
          setLoadingSearch(false);
        }
      }
    };

    if (departurePoint && arrivalPoint && departureDate && cabin) {
      handleSearch();
    }
  }, [arrivalPoint, baggageIncluded, cabin, departureDate, departurePoint, returnDate, handleFlightSearch, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRequest = useCallback(
    (value?: FlightResultRequestOrderCriteriaEnum) => dispatch(flightResults(value || orderCriteria)),
    [dispatch, orderCriteria],
  );

  const roundTrip = useMemo(() => !(departurePoint && !returnDate), [departurePoint, returnDate]);

  return (
    <FlightsList
      isLoading={loadingSearch}
      isLoadingResults={isLoadingResults}
      departurePoint={departurePoint}
      arrivalPoint={arrivalPoint}
      departureDate={departureDate}
      returnDate={returnDate}
      orderCriteria={orderCriteria}
      flightsResult={search?.results || []}
      onRequest={handleRequest}
      flightTabFilter={flightTabFilter}
      currency={search?.currency || 'COP'}
      availableFilters={availableFilters}
      roundTrip={roundTrip}
      cabin={cabin}
      baggageIncluded={baggageIncluded}
      traveler={traveler}
      directFlight={directFlight}
    />
  );
};

export default FlightsListContainer;
